import type { SavedDestination } from "@solomon-apps/shared";
import React, { useState } from "react";

import {
    useCreateSavedDestination,
    useSavedDestinations
} from "@/hooks/useClientSavedDestinations";
import {
    findSavedDestination,
    SAME_AS_SOURCE_LABEL,
    savedDestinationLabel,
    toSavedDestinationInput
} from "@/pages/RewardDestinations/destinationInput";
import {
    isClientConnectionError,
    resolveClientFormError
} from "@/utils/clientFormErrors";

import {
    MULTISIG_VAULT_LABEL,
    resolveMultisigAccountToVault,
    resolveSourceMultisigDestination
} from "../destinationVault";
import type { MultisigSummary, RewardsDestinationVariant } from "../steps";
import { STEP_COLLAPSE } from "../steps";

import { AddressField } from "./AddressField";
import { CollapsingBlock } from "./CollapsingBlock";
import { DestinationLabelField } from "./DestinationLabelField";
import { SavedDestinationCombobox } from "./SavedDestinationCombobox";
import { StepFieldGroup } from "./StepFieldGroup";

const NO_OP = (): void => undefined;

/* ── Step 2 — Set Rewards Destination ───────────────────────────────── */

/**
 * Step 2's set-rewards-destination flow — owns its own expanded↔collapsed
 * transition: on collapse the whole block folds its
 * height away and nothing rests behind. The destination is restated by the
 * last step's review receipt (`ReviewGroup`), so keeping the field as a
 * resting summary would show the same address twice — in the locked MVP the
 * destination IS the position source. The top step gap lives inside the
 * collapsing box so it folds away with the content. Re-expanding (going Back)
 * reverses it.
 *
 * This is the seam for the rewards-destination variants (`variant`):
 *   - "locked" (MVP) — rewards go to the connected source wallet; the
 *     destination field is shown read-only and can't be changed.
 *   - "single" — one custom destination (the original behavior).
 *   - "saved"  — optional pick from the saved Rewards Destinations, where a
 *     new address can be typed straight in and saved (`SavedRewardsFields`).
 *   - "multi"  — multiple destinations (not built yet; falls back to "single").
 */
export interface RewardsGroupProps {
    variant: RewardsDestinationVariant;
    collapsed: boolean;
    positionAddress: string;
    /** The default destination's name (`RewardsSlice.defaultLabel`). */
    defaultLabel?: string;
    multisigSummary: MultisigSummary | null;
    destination: string;
    onDestinationChange: (value: string) => void;
}

export function RewardsGroup({
    variant,
    collapsed,
    positionAddress,
    defaultLabel,
    multisigSummary,
    destination,
    onDestinationChange
}: RewardsGroupProps): React.ReactElement {
    return (
        <CollapsingBlock
            show={!collapsed}
            transition={{
                duration: STEP_COLLAPSE.moveDuration,
                ease: STEP_COLLAPSE.ease
            }}
        >
            {/* pt-6 = the stack's standard step gap below the collapsed source
                summary; it lives inside the collapsing box so the gap folds
                away with the step. */}
            <div className="flex flex-col pt-6">
                <RewardsFields
                    variant={variant}
                    positionAddress={positionAddress}
                    defaultLabel={defaultLabel}
                    multisigSummary={multisigSummary}
                    destination={destination}
                    onDestinationChange={onDestinationChange}
                />
            </div>
        </CollapsingBlock>
    );
}

function RewardsFields({
    variant,
    positionAddress,
    defaultLabel,
    multisigSummary,
    destination,
    onDestinationChange
}: Omit<RewardsGroupProps, "collapsed">): React.ReactElement {
    // MVP: rewards are pinned to the source holding address. The submit
    // payload omits a blank destination, which keeps the wallet's current
    // one, so this branch is UI-only: it shows that address read-only.
    if (variant === "locked") {
        return (
            // `locked`: routing to another wallet is not available, so the
            // step says so. The console uses "saved" today.
            <StepFieldGroup
                collapsed={false}
                heading="Set Rewards Destination Address (Coming Soon)"
                description="We will be enabling this feature shortly."
            >
                <AddressField
                    label="Destination"
                    value={positionAddress}
                    onChange={NO_OP}
                    disabled
                    fill
                />
            </StepFieldGroup>
        );
    }

    if (variant === "saved") {
        return (
            <SavedRewardsFields
                positionAddress={positionAddress}
                defaultLabel={defaultLabel}
                multisigSummary={multisigSummary}
                destination={destination}
                onDestinationChange={onDestinationChange}
            />
        );
    }

    // A Squads source reads like any other: the default is its vault, and
    // pasting its multisig account resolves to that default, so the user
    // never has to tell the two apart.
    // State is lifted to the flow so submit can read the chosen destination.
    return (
        <StepFieldGroup
            collapsed={false}
            heading="Set Rewards Destination Address"
        >
            <AddressField
                label="Destination"
                value={destination}
                onChange={(next) =>
                    onDestinationChange(
                        resolveSourceMultisigDestination(next, multisigSummary)
                    )
                }
                placeholder={`${positionAddress} (${
                    multisigSummary !== null
                        ? MULTISIG_VAULT_LABEL
                        : "same as position"
                })`}
                fill
            />
        </StepFieldGroup>
    );
}

/**
 * The "saved" treatment: a searchable dropdown of the Company Recipient's
 * saved Rewards Destinations. Typing a valid address that isn't saved offers
 * an "Add" row; committing it saves the address as an unlabeled destination
 * and selects it, and a "Label (Optional)" field fades in under the picker to
 * name it (`DestinationLabelField`, which otherwise shows the pick's label
 * read-only).
 * Nothing picked keeps the wallet's current destination (`positionAddress`):
 * the submit payload omits it.
 */
function SavedRewardsFields({
    positionAddress,
    defaultLabel,
    multisigSummary,
    destination,
    onDestinationChange
}: Omit<RewardsGroupProps, "collapsed" | "variant">): React.ReactElement {
    const { destinations } = useSavedDestinations();
    const createDestination = useCreateSavedDestination();
    // The typed-in address being saved, or whose save failed.
    const [draft, setDraft] = useState<string | null>(null);
    // A failed add says why under the field, never as a toast.
    const [addError, setAddError] = useState<string | null>(null);
    // Set only when the save never reached the server: Retry resends it.
    const [retryAdd, setRetryAdd] = useState<(() => void) | null>(null);
    // The saved list refetches before the save resolves; this covers the
    // destination until it does, so the pick never blinks empty.
    const [created, setCreated] = useState<SavedDestination | null>(null);
    // The destination just added from the Add row, while it stays picked:
    // it gets a label field. Picking another or clearing drops it.
    const [labelTarget, setLabelTarget] = useState<SavedDestination | null>(
        null
    );
    const pickerDestinations =
        created !== null && !destinations.some((d) => d.uuid === created.uuid)
            ? [...destinations, created]
            : destinations;

    // The read-only Destination Label: the default's name, a saved
    // destination's label, or nothing (unlabeled, or an add in flight).
    const sourceLabel =
        defaultLabel ??
        (multisigSummary !== null
            ? MULTISIG_VAULT_LABEL
            : SAME_AS_SOURCE_LABEL);
    const trimmedDestination = destination.trim();
    const pickedSaved = findSavedDestination(
        pickerDestinations,
        trimmedDestination
    );
    const pickedLabel =
        draft !== null
            ? ""
            : trimmedDestination === "" ||
                trimmedDestination === positionAddress
              ? sourceLabel
              : pickedSaved
                ? savedDestinationLabel(pickedSaved)
                : "";

    const clearAdd = (): void => {
        setDraft(null);
        setAddError(null);
        setRetryAdd(null);
    };

    const handleCreate = async (typed: string): Promise<void> => {
        // The source's own multisig account is the default (its vault).
        if (resolveSourceMultisigDestination(typed, multisigSummary) === "") {
            clearAdd();
            onDestinationChange("");
            return;
        }
        setDraft(typed);
        setAddError(null);
        setRetryAdd(null);
        try {
            // Any other multisig account is saved as its vault.
            const address = await resolveMultisigAccountToVault(typed);
            if (multisigSummary?.vaultAddress === address) {
                clearAdd();
                onDestinationChange("");
                return;
            }
            // No label: the table shows its short form.
            const saved = await createDestination.mutateAsync(
                toSavedDestinationInput(address, "")
            );
            setCreated(saved);
            setLabelTarget(saved);
            setDraft(null);
            onDestinationChange(saved.address);
        } catch (e) {
            setAddError(
                resolveClientFormError(
                    e,
                    "savedDestination",
                    "Couldn't save destination."
                )
            );
            if (isClientConnectionError(e)) {
                setRetryAdd(() => (): void => void handleCreate(typed));
            }
        }
    };

    return (
        <StepFieldGroup
            collapsed={false}
            heading="Set Rewards Destination Address"
        >
            <SavedDestinationCombobox
                destinations={pickerDestinations}
                sourceAddress={positionAddress}
                sourceLabel={sourceLabel}
                value={destination}
                onChange={(address) => {
                    clearAdd();
                    if (address !== labelTarget?.address) setLabelTarget(null);
                    onDestinationChange(
                        resolveSourceMultisigDestination(
                            address,
                            multisigSummary
                        )
                    );
                }}
                onCreate={(address) => void handleCreate(address)}
                draftAddress={draft}
                creating={draft !== null && addError === null}
                error={addError}
                onRetry={retryAdd ?? undefined}
            />

            <DestinationLabelField
                editable={
                    draft === null &&
                    labelTarget !== null &&
                    destination === labelTarget.address
                        ? labelTarget
                        : null
                }
                label={pickedLabel}
            />
        </StepFieldGroup>
    );
}
